import React, {FC, useState} from "react"
import CopyToClipboard from "react-copy-to-clipboard"
import * as PropTypes from "prop-types"

import {Body, H2, H4} from "@irati/ui/src/Typography"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button/Button"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import authentication from "@irati/auth"
import {getDomain} from "@irati/utils/links"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"

interface Props extends InjectIntl {
  onClick?: () => void
}

const RecognitionChain: FC<Props> = ({onClick, formatMessage}, {apps}) => {
  const [isCopied, setIsCopied] = useState(false)

  const handleCopyClick = () => {
    adminDashboardTracking.valuesCelebrationsActions("cta_recognitioncopyText")
    setIsCopied(true)
  }

  const businessName = authentication.business().businessName
  const copyText = isCopied
    ? "admin.components.dashboard.reporting.costOfLiving.modal.copiedToClipboard"
    : "admin.components.dashboard.reporting.costOfLiving.modal.copyButton"

  return (
    <div>
      <H2 marginBottom={1}>
        <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.chain.title" />
      </H2>
      {isCopied && (
        <Alert
          type={AlertTypes.success}
          title="admin.components.dashboard.reporting.recognition.modal.chain.alert"
          onHide={() => setIsCopied(false)}
        />
      )}
      <Body marginBottom={2} maxWidth="100%">
        <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.chain.body" />
      </Body>
      <Flex justifyContent="center" marginBottom={1}>
        <img width="200px" src={formatMessage({id: "admin.components.dashboard.reporting.recognition.modal.chain.img"})} />
      </Flex>
      <H4 textAlign="center" marginBottom={1}>
        <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.chain.spread" />
      </H4>
      <Body textAlign="center" maxWidth="100%" marginBottom={2}>
        <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.chain.body2" />
      </Body>
      <Body textAlign="center" maxWidth="100%">
        <FormattedMessage
          id="admin.components.dashboard.reporting.recognition.modal.chain.body3"
          values={{
            businessName,
            link: (
              <ButtonRouterLink app={apps.RECOGNITION} tertiary to="/polls/create">
                <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.chain.perkbox" />
              </ButtonRouterLink>
            )
          }}
        />
      </Body>
      <Body textAlign="center" maxWidth="100%" marginBottom={2}>
        <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.chain.body4" />
      </Body>
      <Body textAlign="center" maxWidth="100%" marginBottom={2}>
        <FormattedMessage
          id="admin.components.dashboard.reporting.recognition.modal.chain.body5"
          values={{
            link: (
              <ButtonRouterLink app={apps.RECOGNITION} tertiary to="/polls/create">
                <FormattedMessage id="admin.components.dashboard.reporting.recognition.modal.topCelebrators.here" />
              </ButtonRouterLink>
            )
          }}
        />
      </Body>
      <Flex justifyContent="space-between" alignItems="center">
        <Button tertiary onClick={onClick}>
          <Icon icon={IconTypes.chevronLeft} marginRight />
          <FormattedMessage id="platform.button.back" />
        </Button>
        <CopyToClipboard
          text={formatMessage(
            {
              id: "admin.components.dashboard.reporting.recognition.modal.copyToClipboard"
            },
            {
              businessName,
              link: `${getDomain(authentication.business().subdomain)}/recognition/polls/create`
            }
          )}>
          <Button onClick={handleCopyClick}>
            <FormattedMessage id={copyText} />
          </Button>
        </CopyToClipboard>
      </Flex>
    </div>
  )
}

RecognitionChain.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default injectIntl(RecognitionChain)
